import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useExpandableDropdown } from './expandable-dropdown';
import { cn } from '../../utils/cn';

const expandableDropdownContentVariants = cva(
    'outline-hidden absolute left-0 top-0 z-50 flex flex-col overflow-hidden rounded-2xl bg-white shadow-xl',
    {
        variants: {
            width: {
                auto: 'w-auto',
                sm: 'w-[17.5rem]',
                md: 'w-[22.5rem]',
                lg: 'w-[30rem]',
                full: 'w-full',
            },
            maxHeight: {
                none: '',
                sm: 'max-h-[50vh]',
                md: 'max-h-[70vh]',
                lg: 'max-h-[80vh]',
            },
        },
        defaultVariants: {
            width: 'md',
            maxHeight: 'lg',
        },
    },
);

interface ExpandableDropdownContentProps
    extends
        Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
        VariantProps<typeof expandableDropdownContentVariants> {
    children:
        | React.ReactNode
        | ((props: {
              open: boolean;
              setOpen: (open: boolean) => void;
              triggerRect: DOMRect | null;
              updateTriggerRect: () => void;
          }) => React.ReactNode);
    /** Offset relative to the trigger's top-left corner (e.g. 8, '0.5rem'). */
    xOffset?: number | string;
    yOffset?: number | string;
}

export const ExpandableDropdownContent = React.forwardRef<
    HTMLDivElement,
    ExpandableDropdownContentProps
>(
    (
        {
            className,
            width,
            maxHeight,
            children,
            style,
            xOffset,
            yOffset,
            ...props
        },
        ref,
    ) => {
        const {
            open,
            setOpen,
            contentRef,
            triggerRect,
            updateTriggerRect,
            rootRef,
        } = useExpandableDropdown();

        if (!open) return null;

        const setRef = <T,>(target: React.ForwardedRef<T>, value: T | null) => {
            if (typeof target === 'function') {
                target(value);
                return;
            }
            if (target && 'current' in target) {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                (target as React.MutableRefObject<T | null>).current = value;
            }
        };

        const handleRef = (node: HTMLDivElement | null) => {
            contentRef.current = node;
            setRef(ref, node);
        };

        // Calculate relative position if triggerRect and rootRef are available
        const contentStyle: React.CSSProperties = {
            ...style,
        };

        if (triggerRect && rootRef.current) {
            const rootRect = rootRef.current.getBoundingClientRect();
            const baseLeft = triggerRect.left - rootRect.left;
            const baseTop = triggerRect.top - rootRect.top;

            const buildOffset = (
                baseValue: number,
                offset?: number | string,
            ) => {
                if (offset == null) return baseValue;
                if (typeof offset === 'number') return baseValue + offset;
                return `calc(${baseValue}px + ${offset})`;
            };

            contentStyle.left = buildOffset(baseLeft, xOffset);
            contentStyle.top = buildOffset(baseTop, yOffset);
        }

        return (
            <div
                ref={handleRef}
                className={cn(
                    expandableDropdownContentVariants({ width, maxHeight }),
                    className,
                )}
                style={contentStyle}
                {...props}
            >
                {typeof children === 'function'
                    ? children({
                          open,
                          setOpen,
                          triggerRect,
                          updateTriggerRect,
                      })
                    : children}
            </div>
        );
    },
);

ExpandableDropdownContent.displayName = 'ExpandableDropdownContent';
